import { sharePointHelpers } from '@qdabra/forms-shared/sharePoint';
import { getAttachmentValues } from '@qdabra/infopath-attachments';
import { chain, curry, difference, identity, isEmpty, prop, zip } from 'ramda';
import {
    clearFieldAndSetLinkAsync,
    verifyRequiredAttributesExist,
} from '../helpers/attachmentHelpers';
import {
    sharePointListItemAttributeXPathQRulesLastModified,
    updateFormItemList,
} from '../helpers/listSubmit.helpers';
import { getNodes } from '../helpers/qRules.node.helpers';
import { getItemArray } from '../helpers/sharePoint.getListItemsForSubmit';
import { getIdPath } from '../helpers/sharePointMappingSource';

const { submitListAttachmentAsync } = sharePointHelpers;

const invalidListIdXpath = 'Failed to select node for SharePoint List Id: {0}';
const attachmentWithoutId =
    'Adding attachments requires specifying the ID either in the mapping or the command.';

function submitter(sharePointAccess) {
    // String -> BaseNode(?) -> MappingNode(?)
    const getMappingNodesFromBase = curry((basePath, base) =>
        getNodes(base.node, basePath).map((node) => ({
            parentId: base.id,
            node,
        })),
    );

    // Mapping -> String
    const mappingBasePath = (mapping) =>
        mapping.hasBasePath ? mapping.basePath : '.';

    // ([BaseNode(?)], Mapping) -> MappingNode(?)
    const selectNodesToMap = (baseNodes, mapping) =>
        chain(getMappingNodesFromBase(mappingBasePath(mapping)), baseNodes);

    // Ensures that if there is an ID XPath that at least one of the mapped items
    // has a field at that path
    function checkIdNode(idPath, nodes) {
        if (!idPath) {
            return;
        }

        var filterNodes = nodes.filter((nodeItem) =>
            nodeItem.node.selectSingle(idPath),
        );

        if (!filterNodes.length) {
            throw new Error(String.format(invalidListIdXpath, idPath));
        }
    }

    function getAttachmentXPath(mapping, attachXPath) {
        //as of 4.2, indicating attachment field in the mapping so that we can loop through the mapping to submit to all lists.
        //if both are included, defaulting to the param

        if (!attachXPath) {
            var attachFieldFilter = mapping
                .getFields()
                .filter(function (fieldItem) {
                    return fieldItem.isAttachment;
                });

            if (attachFieldFilter.length > 0) {
                attachXPath = attachFieldFilter[0].xPath;
            }
        }

        return attachXPath;
    }

    function checkHasAttachments(attachDsName, attachXPath) {
        //if there is a dsname and an xpath for including attachments, there must be an id - we have to have the item ID to know what to attach to
        return attachDsName && attachXPath;
    }

    function checkAttachmentPreReqs(attachXPath, attachDsName, idXPath) {
        var hasAttachments = checkHasAttachments(attachDsName, attachXPath);

        //data connection is verified in Attach to ShP List method
        if (hasAttachments) {
            if (!idXPath) {
                throw new Error(attachmentWithoutId);
            }
        }

        return hasAttachments;
    }

    const processAttachmentFileAsync = async (
        attachmentNode,
        itemId,
        container,
        clearAttachment,
    ) => {
        const fileValue = attachmentNode.value();

        if (!fileValue) {
            return;
        }

        verifyRequiredAttributesExist(attachmentNode);

        const { fileName, base64 } = getAttachmentValues(fileValue);
        const { siteUrl, listId, listName } = container;
        const useListName = !listId;

        const data = await submitListAttachmentAsync(
            sharePointAccess,
            itemId,
            fileName,
            base64,
            siteUrl,
            listId || listName,
            useListName,
        );

        await clearFieldAndSetLinkAsync(
            attachmentNode,
            data.fileName,
            data.attachmentUrl,
            clearAttachment,
        );
    };

    const processItemAttachments = async (
        item,
        itemId,
        attachXPath,
        container,
        clearAttachment,
    ) => {
        const attachmentNodes = item.node.node.selectNodes(attachXPath);

        for (const node of attachmentNodes) {
            await processAttachmentFileAsync(
                node,
                itemId,
                container,
                clearAttachment,
            );
        }
    };

    const processItemsAttachment = async (
        pairs,
        attachXPath,
        container,
        clearAttachment,
    ) => {
        for (const { item, id } of pairs) {
            await processItemAttachments(
                item,
                id,
                attachXPath,
                container,
                clearAttachment,
            );
        }
    };

    const setLastModifiedAsync = async (idNode, modified) => {
        const lastModifiedNode = idNode.selectSingle(
            sharePointListItemAttributeXPathQRulesLastModified,
        );

        if (lastModifiedNode) {
            await lastModifiedNode.setValueAsync(modified);
        }
    };

    const setItemIdAsync = async (idxPath, item, id, modified) => {
        const idNode = idxPath && item.node.node.selectSingle(idxPath);

        if (idNode) {
            await idNode.setValueAsync(id);

            await setLastModifiedAsync(idNode, modified);
        }
    };

    async function populateIdsAndModified(
        itemArray,
        resultData,
        idxPath,
        attachXPath,
        container,
        clearAttachment,
        hasAttachments,
    ) {
        const pairs = zip(itemArray, resultData).map(
            ([item, { id, modified }]) => ({ item, id, modified }),
        );

        for (const { item, id, modified } of pairs) {
            await setItemIdAsync(idxPath, item, id, modified);
        }

        if (hasAttachments) {
            await processItemsAttachment(
                pairs,
                attachXPath,
                container,
                clearAttachment,
            );
        }

        return {
            success: true,
            items: pairs.map(({ item, id }) => ({ node: item.node.node, id })),
        };
    }

    async function processChildMappingsAsync(mapping, settings, baseItems) {
        const childMappings = settings.mappings.getChildMappings(mapping);
        let errors = [];

        for (const childMapping of childMappings) {
            // eslint-disable-next-line no-use-before-define -- these functions recursively call each other to traverse down the mapping relationships
            const childErrors = await performSubmitAsync(
                childMapping,
                settings,
                baseItems,
            );

            errors = errors.concat(childErrors);
        }

        return errors;
    }

    const itemDeleteErrorMessage = ({ id, error }) =>
        `Error deleting item with ID ${id}: ${error}`;

    // (ListMapping, [MappingNode], String) => Promise[[String]]
    async function processDeletesAsync(mapping, mapNodes, idxPath) {
        if (!idxPath) {
            return [];
        }

        const knownIds = mapping.baseNode
            .selectNodes('SharePointItems/SharePointItem/Id')
            .map((n) => n.value());

        const presentIds = mapNodes
            .map((n) => n.node.selectSingle(idxPath))
            .filter(identity)
            .map((n) => n.value());

        const missingIds = difference(knownIds, presentIds);

        if (isEmpty(missingIds)) {
            return [];
        }

        const list = mapping.listName;
        const listIdentifier = list.useName ? list.name : list.id;

        try {
            const { failedIds } = await sharePointAccess.deleteListItemsAsync(
                mapping.listUrl,
                listIdentifier,
                list.useName,
                missingIds,
            );

            return failedIds.map(itemDeleteErrorMessage);
        } catch (e) {
            console.error('Error deleting items', e);

            return [e.message];
        }
    }

    const updateFormItemsAsync = async (mappedNodes, idxPath, mapping) => {
        if (idxPath && mapping.baseNode.selectSingle('SharePointItems')) {
            await updateFormItemList(
                mappedNodes.map(prop('node')),
                idxPath,
                mapping,
            );
        }
    };

    async function submitAndPopulateIds(
        container,
        itemArray,
        idxPath,
        attachXPath,
        clearAttachment,
        hasAttachments,
        list,
    ) {
        try {
            const data = await sharePointAccess.submitToListAsync(container);

            return await populateIdsAndModified(
                itemArray,
                data,
                idxPath,
                attachXPath,
                container,
                clearAttachment,
                hasAttachments,
            );
        } catch (error) {
            console.error(error);

            return {
                success: false,
                error: `Encountered error when submitting to list ${
                    list.useName ? list.name : list.id
                }: ${error.message}`,
            };
        }
    }

    // [Mapping, Settings, [Node]] -> Promise[String]
    async function performSubmitAsync(mapping, settings, baseNodes) {
        const idxPath = getIdPath(mapping, settings.idxPath);
        const nodesToMap = selectNodesToMap(baseNodes, mapping);
        const hasDeletes =
            mapping.baseNode.selectNodes('SharePointItems/SharePointItem')
                .length !== 0;

        if (!nodesToMap.length && !hasDeletes) {
            // Nothing to map. Log message?
            return [];
        }

        //only check id if there are nodes to map
        if (nodesToMap.length) {
            checkIdNode(idxPath, nodesToMap);
        }

        const {
            xPath: settingsAttachXPath,
            dsName: settingsAttachDsName,
            clear: clearAttachment,
        } = settings.attachSettings;
        const attachXPath = getAttachmentXPath(mapping, settingsAttachXPath);
        const hasAttachments = checkAttachmentPreReqs(
            attachXPath,
            settingsAttachDsName,
            idxPath,
        );

        const itemArray = getItemArray(nodesToMap, mapping, idxPath);
        const list = mapping.listName;
        const container = {
            itemArray: itemArray,
            siteUrl: mapping.listUrl,
            listId: list.id,
            listName: list.name,
            useName: list.useName,
        };

        const data = await submitAndPopulateIds(
            container,
            itemArray,
            idxPath,
            attachXPath,
            clearAttachment,
            hasAttachments,
            list,
        );

        if (!data.success) {
            return [data.error];
        }

        const deleteErrors = await processDeletesAsync(
            mapping,
            nodesToMap,
            idxPath,
        );

        await updateFormItemsAsync(nodesToMap, idxPath, mapping);

        const childMappingErrors = await processChildMappingsAsync(
            mapping,
            settings,
            data.items,
        );

        return [...deleteErrors, ...childMappingErrors];
    }

    return {
        performSubmitAsync,
    };
}

export default submitter;
